const totalTravelTimeNode = `
  days
  hours
  minutes
`;

const portalAirportNode = `
  cityName
  locationCode
  locationName
`;

const portalAirportSlimNode = `
  cityName
  locationCode
`;

const portalAirlineConfirmationNode = `
  shortName
  webSite
`;

const portalAirlineSegmentNode = `
  logoUrl
  shortName
`;

const portalPassengerNode = `
  id
  countryId
  firstName
  middleName
  lastName
  fullName
  birthday
  gender
  passport {
    expiration
    number
    country {
      name
    }
  }
`;

const portalETicketPassengerNode = `
  firstName
  middleName
  lastName
  fullName
  birthday
  gender
  passport {
    expiration
    number
    country {
      name
    }
  }
`;

const portalAncillaryNode = `
  type
  price
  amountPerTraveler
  refundPerTraveler
  balanceRefundPerTraveler
  status
`;

const portalFlightItinerarySegmentNode = `
  flightSegment {
    departureAtAirportTimeZone
    arrivalAtAirportTimeZone
    serviceClass
    airlineServiceClass
    airline {
      ${portalAirlineSegmentNode}
    }
    flightNumber
    aircraftName
    isOvernight
    operatedBy
    departureAirport {
      ${portalAirportNode}
    }
    arrivalAirport {
      ${portalAirportNode}
    }
  }
  layover {
    waitTime {
      ${totalTravelTimeNode}
    }
    selfTransfer
  }
  travelTime {
    ${totalTravelTimeNode}
  }
  nextDayArrival
`;

const ancillaryBundleNode = `
  priceWithoutDiscount
  discount
  price
  pricePerTraveler
  priceWithoutDiscountPerTraveler
  ancillaries {
    type
    price
    priceWithoutDiscount
    pricePerTraveler
    priceWithoutDiscountPerTraveler
  }
`;

const availableUpsellOptionsNode = `
  essential
  vip
  executive
  gratuity
`;

const salesAgentNode = `
  fullName
  email
  phone
  picture
  deals
  avgSavings
  portalFeatures
  shift {
    startDateTimeAtUtc
    endDateTimeAtUtc
  }
  currency {
    name
  }
  agencyName
`;

const salesAgentHeaderNode = `
  fullName
  email
  phone
  picture
  portalFeatures
  shift {
    startDateTimeAtUtc
    endDateTimeAtUtc
  }
`;

const salesAgentAutoQuoteNode = `
  fullName
  phone
  picture
`;

const portalFlightItinerariesNode = `
  travelTime {
    ${totalTravelTimeNode}
  }
  departureAirport {
    ${portalAirportSlimNode}
  }
  arrivalAirport {
    ${portalAirportSlimNode}
  }
  finalDestinationAirport {
    ${portalAirportSlimNode}
  }
  departureAt
  daysShift
  segments {
    ${portalFlightItinerarySegmentNode}
  }
`;

const portalOrderBookingCardNode = `
  portalLink
  tripType
  airlineConfirmations {
    code
  }
  flightItineraries {
    departureAirport {
      cityName
    }
    arrivalAirport {
      cityName
    }
    segments {
      flightSegment {
        airline {
          logoUrl
        }
      }
    }
  }
  bookingHotel {
    name
    guestCount
    checkInDate
    checkoutDate
    city
  }
`;

const referralCreditNode = `
  status
  amount
  expiresAt
`;

const portalOrderReferralFields = `
  referralCredit {
    ${referralCreditNode}
  }
`;

const portalOrderNode = (includeReferralCredit = true) => `
  portalLink
  ${includeReferralCredit ? portalOrderReferralFields : ''}
  cashback {
    eligible
    amount
  }
  acceptStripe
  alphaName
  portalDescription
  soldAmount
  onlinePrice
  savePrice
  bookingStatus
  hasThrowawayTicketing
  hasHiddenCityTicketing
  hiddenFlightsPresented
  roundTripDepartureAt
  tripType
  flightItineraries {
    ${portalFlightItinerariesNode}
  }
  hotelBookings {
    name
    guestCount
    city
    checkInDate
    checkoutDate
  }
  ancillaries {
    ${portalAncillaryNode}
  }
  numberOfAdults
  numberOfChildren
  numberOfInfants
  soldAmountPerAdult
  soldAmountPerChild
  soldAmountPerInfant
  flightsTotal
  totalPassengers
  disclaimersHtml
  disclaimersCheckmarkHtml
  disclaimersWithAdditionalProductsHtml
  isDemo
  isChange
  changeFeePerPax
  voluntaryChangeFeeMax
  maxBalanceAmount
  serviceClass
  fastest
  expertChoice
  ancillaryBundle {
    ${ancillaryBundleNode}
  }
`;

// Slim itinerary segment shape for ``portalLead`` publishedOptions — only
// what ``option-itinerary`` / ``logos-container`` on the Quote card reads
// (airline logo, arrival airport code, per-segment timezones, layover wait
// time for the "3h in MUC" label, service class for the cabin badge built
// by ``extractServiceClasses`` + ``service-class-badge``). Detail-only
// fields (aircraft name, operated-by, next day arrival, airline shortName)
// live only on ``portalFlightItinerarySegmentNode`` used by
// ``option-details`` on downstream pages.
const portalFlightItinerarySegmentQuoteCardNode = `
  flightSegment {
    departureAtAirportTimeZone
    arrivalAtAirportTimeZone
    serviceClass
    airline {
      logoUrl
    }
    arrivalAirport {
      locationCode
    }
  }
  layover {
    waitTime {
      ${totalTravelTimeNode}
    }
    selfTransfer
  }
`;

const portalFlightItinerariesQuoteCardNode = `
  travelTime {
    ${totalTravelTimeNode}
  }
  departureAirport {
    ${portalAirportSlimNode}
  }
  finalDestinationAirport {
    ${portalAirportSlimNode}
  }
  daysShift
  segments {
    ${portalFlightItinerarySegmentQuoteCardNode}
  }
`;

// Slim order shape for ``portalLead`` publishedOptions — every field the
// Quote card (``option.component`` + ``logos-container`` + ``option-itinerary``
// + ``prices-tooltip``) actually reads. Fields required only by the
// option / checkout / trip pages (disclaimers, ancillary bundle, ancillaries,
// stripe / balance / change-fee metadata, per-segment details) are fetched
// separately via ``portalOrder(portalLink)`` when the user picks an option
// (TIC-6859). Keeping this shape narrow is what keeps the Quote payload
// bounded no matter how many options a lead has.
const portalOrderQuoteCardNode = `
  portalLink
  alphaName
  portalDescription
  cashback {
    eligible
    amount
  }
  savePrice
  onlinePrice
  soldAmount
  soldAmountPerAdult
  soldAmountPerChild
  soldAmountPerInfant
  numberOfAdults
  numberOfChildren
  numberOfInfants
  flightsTotal
  totalPassengers
  bookingStatus
  isChange
  expertChoice
  fastest
  hotelBookings {
    name
    guestCount
    city
    checkInDate
    checkoutDate
  }
  flightItineraries {
    ${portalFlightItinerariesQuoteCardNode}
  }
`;

const portalOrderTripNode = (includeReferralCredit = true) => `
  ${portalOrderNode(includeReferralCredit)}
  promocodeAmount
  tripPortalLink
  salesAgent {
    ${salesAgentNode}
  }
  bookings {
    ${portalOrderBookingCardNode}
  }
`;

const portalCreditCardNode = `
  id
  number
  holder
  expiration
  type
`;

const portalUserVerificationNode = `
  required
  emailMasked
  emailSentAt
`;

const authorizedDataNode = `
  passengers {
    ${portalPassengerNode}
    frequentFlyerNumbers {
      number
      airlineId
    }
    emails
    phones
    globalEntry
    knownTravelerNumber
    specialAssistanceId
    petsId
    mealPreferencesId
    seatPreferencesId
  }
  creditCards {
    ${portalCreditCardNode}
  }
  stripeCreditCards {
    ${portalCreditCardNode}
  }
  verification {
    ${portalUserVerificationNode}
  }
  clientBalanceAmount
  creditBreakdown {
    remainingAmount
    expiresAt
  }
`;

const firstTripNode = `
  departureAt
  serviceClass
  passengerCount
  departureAirport {
    ${portalAirportSlimNode}
  }
  arrivalAirport {
    ${portalAirportSlimNode}
  }
`;

const tripOrderCardNode = `
  portalLink
  tripPortalLink
  tripType
  totalPassengers
  roundTripDepartureAt
  numberOfAdults
  numberOfChildren
  numberOfInfants
  serviceClass
  salesAgent {
    fullName
    picture
  }
  flightItineraries {
    departureAt
    departureAirport {
      cityName
      locationCode
    }
    arrivalAirport {
      cityName
      locationCode
    }
  }
`;

const googleFlightAirportNode = `
  code
  name
  time
`;

const googleFlightLegNode = `
  departure_airport: departureAirport {
    ${googleFlightAirportNode}
  }
  arrival_airport: arrivalAirport {
    ${googleFlightAirportNode}
  }
  duration
  stops
  stop_info: stopInfo
`;

const portalAutoQuoteOptionNode = `
  display_price: displayPrice
  is_round_trip: isRoundTrip
  travel_class: travelClass
  airlines
  airline_logos: airlineLogos
  clientFeedback
  optionName
  outbound {
    ${googleFlightLegNode}
  }
  inbound {
    ${googleFlightLegNode}
  }
`;

const portalAutoQuoteNode = `
  token
  status
  additionalFeedback
  options {
    ${portalAutoQuoteOptionNode}
  }
  expired
  departureAirportCode
  arrivalAirportCode
  departureCityName
  arrivalCityName
  isRoundTrip
  passengerCount
  salesAgent {
    ${salesAgentAutoQuoteNode}
  }
`;

const portalLeadReferralFields = `
  referralCredit {
    ${referralCreditNode}
  }
`;

const portalLeadNode = (includeReferralCredit = true) => `
  portalLink
  ${includeReferralCredit ? portalLeadReferralFields : ''}
  firstName
  lastName
  isNewUser
  salesAgent {
    ${salesAgentNode}
  }
  firstTrip {
    ${firstTripNode}
  }
  flightRequest {
    isLost
    portalLink
    publishedOptions {
      edges {
        node {
          ${portalOrderQuoteCardNode}
        }
      }
    }
    client {
      phone
    }
  }
  roundTripDepartureAt
  tripType
  hasNotes
  isQuickquote
`;

export const portalLead = (options?: { includeReferralCredit?: boolean }) => {
  const includeReferralCredit = options?.includeReferralCredit !== false;

  return `
    query PortalLead($portalLink: ID!) {
      portalLead(portalLink: $portalLink) {
        ${portalLeadNode(includeReferralCredit)}
      }
    }
  `;
};

// On option-select, the SPA calls this to load the full ``portalOrder``
// shape into ``PortalOrderService`` before navigating. Runs on the public
// ``/portal/graphql`` schema (same passcode gate as ``portalLead``), so the
// unauth Quote-page flow works.
export const portalOrder = (options?: { includeReferralCredit?: boolean }) => {
  const includeReferralCredit = options?.includeReferralCredit !== false;

  return `
    query PortalOrder($portalLink: ID!) {
      portalOrder(portalLink: $portalLink) {
        ${portalOrderNode(includeReferralCredit)}
      }
    }
  `;
};

export const portalAutoQuote = () => {
  return `
    query PortalAutoQuote($token: ID!) {
      portalAutoQuote(token: $token) {
        ${portalAutoQuoteNode}
      }
    }
  `;
};

export const portalQuoteAccessState = () => {
  return `
    query PortalQuoteAccessState($portalLink: ID!) {
      portalQuoteAccessState(portalLink: $portalLink) {
        isLocked
      }
    }
  `;
};

export const setPortalAutoQuoteOptionFeedback = () => {
  return `
    mutation SetPortalAutoQuoteOptionFeedback($input: SetPortalAutoQuoteOptionFeedbackInput!) {
      setPortalAutoQuoteOptionFeedback(input: $input) {
        ok
        status
        error
        autoQuote {
          ${portalAutoQuoteNode}
        }
      }
    }
  `;
};

export const submitPortalAutoQuoteAdditionalFeedback = () => {
  return `
    mutation SubmitPortalAutoQuoteAdditionalFeedback($input: SubmitPortalAutoQuoteAdditionalFeedbackInput!) {
      submitPortalAutoQuoteAdditionalFeedback(input: $input) {
        ok
        status
        error
        autoQuote {
          ${portalAutoQuoteNode}
        }
      }
    }
  `;
};

export const unlockPortalQuote = () => {
  return `
    mutation UnlockPortalQuote($input: UnlockPortalQuoteInput!) {
      unlockPortalQuote(input: $input) {
        ok
        error
        token
        role
      }
    }
  `;
};

export const portalUpdateOption = () => {
  return `
    mutation portalUpdateOption($inputUpdate: PortalUpdateOptionInput) {
      portalUpdateOption(inputUpdate: $inputUpdate) {
        ok
      }
    }
  `;
};

export const portalSubmitOption = () => {
  return `
    mutation PortalSubmitOption($input: PortalSubmitOptionInput) {
      portalSubmitOption(input: $input) {
        ok
        errors
        stripeClientSecret
      }
    }
  `;
};

export const portalSubmitPassengers = () => {
  return `
    mutation PortalSubmitPassengers($input: PortalSavePassengersInput!) {
      portalSubmitPassengers(input: $input) {
        ok
        errors
        passengers {
          id
          countryId
          firstName
          middleName
          lastName
          fullName
          birthday
          gender
        }
      }
    }
  `;
};

export const portalSetHiddenFlightsPresented = (options?: { includeReferralCredit?: boolean }) => {
  const includeReferralCredit = options?.includeReferralCredit !== false;

  return `
    mutation PortalSetHiddenFlightsPresented($portalLink: ID!, $hiddenFlightsPresented: Boolean) {
      portalSetHiddenFlightsPresented(portalLink: $portalLink, hiddenFlightsPresented: $hiddenFlightsPresented) {
        ok
        result {
          ${portalOrderNode(includeReferralCredit)}
        }
      }
    }
  `;
};

export const authorizedData = () => {
  return `
    query AuthorizedData($portalLink: ID!) {
      authorizedData(portalLink: $portalLink) {
        ${authorizedDataNode}
        quotes {
          portalLink
          firstTrip {
            ${firstTripNode}
          }
          roundTripDepartureAt
          tripType
          optionCount
          minimalOptionPrice
          maximalSecureSavings
        }
        upcomingFlightOrders {
          edges {
            node {
              ${tripOrderCardNode}
            }
          }
        }
        pastFlightOrders {
          edges {
            node {
              ${tripOrderCardNode}
            }
          }
        }
      }
    }
  `;
};

export const portalSendVerificationEmail = () => {
  return `
    mutation PortalSendVerificationEmail($portalLink: ID!) {
      portalSendVerificationEmail(portalLink: $portalLink) {
        ok
        result {
          ${authorizedDataNode}
        }
      }
    }
  `;
};

export const portalAcceptVerification = () => {
  return `
    mutation PortalAcceptVerification($portalLink: ID!, $verificationCode: String!) {
      portalAcceptVerification(portalLink: $portalLink, verificationCode: $verificationCode) {
        ok
        result {
          ${authorizedDataNode}
          upcomingFlightOrders {
            edges {
              node {
                ${tripOrderCardNode}
              }
            }
          }
          pastFlightOrders {
            edges {
              node {
                ${tripOrderCardNode}
              }
            }
          }
        }
      }
    }
  `;
};

export const sendPasswordToEmail = () => {
  return `
    mutation SendPasswordToEmail($portalLink: ID!, $email: Email!) {
      sendPasswordToEmail(portalLink: $portalLink, email: $email) {
        ok
      }
    }
  `;
};

export const portalOptionSelected = () => {
  return `
    mutation PortalOptionSelected($portalLink: ID!) {
      portalOptionSelected(portalLink: $portalLink) {
        ok
      }
    }
  `;
};

export const portalFinishPayment = () => {
  return `
    mutation PortalFinishPayment($portalLink: ID!) {
      portalFinishPayment(portalLink: $portalLink) {
        ok
      }
    }
  `;
};

export const validatePromocode = () => {
  return `
    mutation ValidatePromocode($input: ValidatePromocodeInput!) {
      validatePromocode(input: $input) {
        ok
        errors
        discountAmount
      }
    }
  `;
};

export const portalNewLead = () => {
  return `
    mutation PortalNewLead($input: PortalNewLeadInput!) {
      portalNewLead(input: $input) {
        ok
        errors
        portalLink
      }
    }
  `;
};

export const portalAuthorizedBooking = () => {
  return `
    query PortalAuthorizedBooking($portalLink: ID!) {
      portalAuthorizedBooking(portalLink: $portalLink) {
        booking {
          portalLink
          tripType
          flightItineraries {
            travelTime {
              ${totalTravelTimeNode}
            }
            departureAt
            daysShift
            segments {
              ${portalFlightItinerarySegmentNode}
            }
            finalDestinationAirport {
              ${portalAirportSlimNode}
            }
            departureAirport {
              ${portalAirportSlimNode}
            }
            arrivalAirport {
              ${portalAirportSlimNode}
            }
          }
          bookingHotel {
            name
            guestCount
            checkInDate
            checkoutDate
            city
          }
          eTickets {
            codeNumber
            passenger {
              ${portalETicketPassengerNode}
            }
          }
          airlineConfirmations {
            code
            airline {
              ${portalAirlineConfirmationNode}
            }
          }
        }
        verification {
          ${portalUserVerificationNode}
        }
      }
    }
  `;
};

export const portalAuthorizedOrder = (options?: { includeReferralCredit?: boolean }) => {
  const includeReferralCredit = options?.includeReferralCredit !== false;

  return `
    query PortalAuthorizedOrder($portalLink: ID!) {
      portalAuthorizedOrder(portalLink: $portalLink) {
        order {
          ${portalOrderTripNode(includeReferralCredit)}
        }
        verification {
          ${portalUserVerificationNode}
        }
      }
    }
  `;
};

export const updatePassenger = () => {
  return `
    mutation UpdatePassenger($inputData: PortalPassengerInput!) {
      updatePassenger(inputData: $inputData) {
        ok
      }
    }
  `;
};

export const portalTripAncillaryUpsellBanner = () => {
  return `
    query PortalTripAncillaryUpsellBanner($tripPortalLink: ID!) {
      portalTripAncillaryUpsell(tripPortalLink: $tripPortalLink) {
        emailVariant
        timerExpiresAt
      }
    }
  `;
};

export const portalTripAncillaryUpsell = () => {
  return `
    query PortalTripAncillaryUpsell($tripPortalLink: ID!, $perpetual: Boolean) {
      portalTripAncillaryUpsell(tripPortalLink: $tripPortalLink, perpetual: $perpetual) {
        flightItineraries {
          departureAirport {
            locationCode
            cityName
          }
          finalDestinationAirport {
            locationCode
            cityName
          }
          departureAt
          segments {
            flightSegment {
              airline {
                logoUrl
              }
            }
          }
        }
        ancillaries {
          ${portalAncillaryNode}
        }
        ancillaryBundle {
          ${ancillaryBundleNode}
        }
        salesAgent {
          ${salesAgentHeaderNode}
        }
        acceptStripe
        disclaimersHtml
        disclaimersCheckmarkHtml
        disclaimersWithAdditionalProductsHtml
        numberOfAdults
        numberOfChildren
        numberOfInfants
        showGratuity
        soldAmountPerAdult
        clientPhone
        availableUpsellOptions {
          ${availableUpsellOptionsNode}
        }
      }
    }
  `;
};

export const portalAncillariesUpsell = () => {
  return `
    mutation PortalAncillariesUpsell($input: PortalAncillariesUpsellInput!) {
      portalAncillariesUpsell(input: $input) {
        ok
        errors
        stripeClientSecret
      }
    }
  `;
};

export const saveGoogleFlightsBaselineFeedback = () => {
  return `
    mutation SaveGoogleFlightsBaselineFeedback($input: SaveGoogleFlightsBaselineFeedbackInput!) {
      saveGoogleFlightsBaselineFeedback(input: $input) {
        ok
        errors
        portalLead {
          ${portalLeadNode(true)}
        }
      }
    }
  `;
};

export const portalTierCatalog = () => {
  return `
    query PortalTierCatalog {
      portalTierCatalog {
        tierType
        cashbackPerPassenger
        coverageBands {
          priceThreshold
          coverageAmount
        }
      }
    }
  `;
};

export const portalReferralProgram = () => {
  return `
    query PortalReferralProgram {
      portalReferralProgram {
        isEligible
        hasAccess
        code
        shareableUrl
        referrerFirstName
        rewardAmount
        refereeWindowDays
        cap
        usedCount
        referrals {
          id
          maskedName
          referredAt
          displayStatus
          daysLeftToBook
          rewardAmount
        }
      }
    }
  `;
};

export const portalReferralCreditPreview = () => {
  return `
    query PortalReferralCreditPreview($portalLink: ID!, $optionId: ID!, $passengers: [PortalPassengerNodeInput!]!) {
      portalReferralCreditPreview(portalLink: $portalLink, optionId: $optionId, passengers: $passengers) {
        applies
        amount
        notAppliedReason
      }
    }
  `;
};
